// packages/web/src/lib/api/circuit-breaker.ts
// Circuit Breaker implementation for API safety at scale
// Prevents cascade failures when services are down

export type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

export interface CircuitBreakerConfig {
  failureThreshold: number; // Number of failures before opening
  recoveryTimeout: number; // Time before trying again (ms)
  monitoringPeriod: number; // How long to track failures (ms)
  successThreshold: number; // Successes needed to close circuit
}

export interface CircuitBreakerStats {
  state: CircuitState;
  failures: number;
  successes: number;
  lastFailureTime: number;
  lastSuccessTime: number;
  totalRequests: number;
  totalFailures: number;
}

export class CircuitBreaker {
  private failures = 0;
  private successes = 0;
  private consecutiveSuccesses = 0;
  private lastFailureTime = 0;
  private lastSuccessTime = 0;
  private totalRequests = 0;
  private totalFailures = 0;
  private state: CircuitState = 'CLOSED';

  constructor(private config: CircuitBreakerConfig) {}

  async execute<T>(operation: () => Promise<T>): Promise<T> {
    this.totalRequests++;

    if (this.state === 'OPEN') {
      if (this.shouldAttemptReset()) {
        this.state = 'HALF_OPEN';
        console.log(`🔄 CIRCUIT BREAKER: Attempting reset for operation`);
      } else {
        const timeUntilReset = this.config.recoveryTimeout - (Date.now() - this.lastFailureTime);
        throw new Error(
          `Circuit breaker is OPEN. Next attempt in ${Math.ceil(timeUntilReset / 1000)}s`,
        );
      }
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      // Don't count 402 Payment Required as a failure - it's expected for unpaid partners
      if (error instanceof Error && error.message === '__CIRCUIT_BREAKER_402_PAYMENT_REQUIRED__') {
        console.log(
          `[CircuitBreaker] 402 Payment Required - not counting as circuit breaker failure`,
        );
        // Re-throw so the caller can handle it appropriately
        throw error;
      }
      this.onFailure();
      throw error;
    }
  }

  private onSuccess() {
    this.successes++;
    this.consecutiveSuccesses++;
    this.lastSuccessTime = Date.now();

    if (this.state === 'HALF_OPEN' && this.consecutiveSuccesses >= this.config.successThreshold) {
      this.reset();
    }
  }

  private onFailure() {
    this.failures++;
    this.totalFailures++;
    this.consecutiveSuccesses = 0;
    this.lastFailureTime = Date.now();

    if (this.state === 'CLOSED' && this.failures >= this.config.failureThreshold) {
      this.trip();
    } else if (this.state === 'HALF_OPEN') {
      this.trip(); // Failed during recovery attempt
    }
  }

  private shouldAttemptReset(): boolean {
    return Date.now() - this.lastFailureTime >= this.config.recoveryTimeout;
  }

  private trip() {
    this.state = 'OPEN';
    console.error(
      `🚨 CIRCUIT BREAKER TRIPPED: ${this.failures} failures in ${this.config.monitoringPeriod}ms`,
    );
    console.error(`   Total requests: ${this.totalRequests}, Failures: ${this.totalFailures}`);
  }

  private reset() {
    this.state = 'CLOSED';
    this.failures = 0;
    this.consecutiveSuccesses = 0;
    console.log(`✅ CIRCUIT BREAKER RESET: Service recovered`);
  }

  getStats(): CircuitBreakerStats {
    return {
      state: this.state,
      failures: this.failures,
      successes: this.successes,
      lastFailureTime: this.lastFailureTime,
      lastSuccessTime: this.lastSuccessTime,
      totalRequests: this.totalRequests,
      totalFailures: this.totalFailures,
    };
  }

  // Manual controls for testing/admin
  forceOpen() {
    this.state = 'OPEN';
    this.lastFailureTime = Date.now();
  }

  forceClose() {
    this.reset();
  }

  forceHalfOpen() {
    this.state = 'HALF_OPEN';
  }
}

// Global circuit breaker registry
const circuitBreakers = new Map<string, CircuitBreaker>();

export function getCircuitBreaker(
  serviceName: string,
  config?: Partial<CircuitBreakerConfig>,
): CircuitBreaker {
  if (!circuitBreakers.has(serviceName)) {
    const defaultConfig: CircuitBreakerConfig = {
      failureThreshold: 3, // Reduced from 5 to prevent premature tripping
      recoveryTimeout: 30000, // Reduced from 60000 (30s instead of 1min)
      monitoringPeriod: 120000, // Reduced from 300000 (2min instead of 5min)
      successThreshold: 2, // Reduced from 3 for faster recovery
    };

    const finalConfig = { ...defaultConfig, ...config };
    circuitBreakers.set(serviceName, new CircuitBreaker(finalConfig));
  }

  return circuitBreakers.get(serviceName)!;
}

export function getAllCircuitBreakerStats(): Record<string, CircuitBreakerStats> {
  const stats: Record<string, CircuitBreakerStats> = {};
  for (const [name, breaker] of circuitBreakers) {
    stats[name] = breaker.getStats();
  }
  return stats;
}

// Token validation helper
export function isValidToken(token: string | null): boolean {
  if (!token || typeof token !== 'string' || token.trim() === '') {
    return false;
  }
  // Basic JWT format check (header.payload.signature)
  const parts = token.split('.');
  return parts.length === 3 && parts.every((part) => part.length > 0);
}

// Circuit breaker-aware API fetch
export async function apiFetchWithCircuitBreaker(
  path: string,
  options?: RequestInit,
  serviceName?: string,
  operation?: () => Promise<any>,
): Promise<any> {
  // Import getApiBaseUrl dynamically to avoid circular imports
  const { getApiBaseUrl } = await import('../../utils/apiBase');
  const baseUrl = getApiBaseUrl();
  // Ensure path is always relative to avoid absolute URL issues
  let relativePath = path;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    // Extract the path part from absolute URLs
    try {
      const url = new URL(path);
      relativePath = url.pathname + url.search;
      console.warn(
        '[CircuitBreaker] Converting absolute URL to relative:',
        path,
        '->',
        relativePath,
      );
    } catch (error) {
      console.error('[CircuitBreaker] Invalid URL format:', path);
      throw new Error(`Invalid URL format: ${path}`);
    }
  }

  // Construct full URL
  const fullUrl = baseUrl + relativePath.replace('/api', '');

  // Extract service name from path (e.g., /api/partners/banking → banking)
  const inferredService = serviceName || relativePath.split('/api/')[1]?.split('/')[0] || 'default';

  const breaker = getCircuitBreaker(inferredService);

  // Use provided operation or default fetch
  const defaultOperation = async () => {
    // Add authentication headers like RTK Query does
    const headers = new Headers(options?.headers || {});

    // Add authentication token if available
    if (typeof window !== 'undefined') {
      try {
        let token: string | null = null;
        let tokenSource = 'none';

        // Check Redux store first
        const reduxStore = (window as any).store;
        if (reduxStore) {
          const state = reduxStore.getState();
          const reduxToken = state?.auth?.token;
          if (reduxToken && isValidToken(reduxToken)) {
            token = reduxToken;
            tokenSource = 'redux';
          }
        }

        // Fallback to localStorage if no valid Redux token
        if (!token) {
          const localStorageToken = window.localStorage.getItem('token');
          if (localStorageToken && isValidToken(localStorageToken)) {
            token = localStorageToken;
            tokenSource = 'localStorage';
          }
        }

        // Fallback to cookies if no valid token found
        if (!token) {
          const cookies = document.cookie.split(';');
          const tokenCookie = cookies.find((c) => c.trim().startsWith('token='));
          if (tokenCookie) {
            const tokenValue = decodeURIComponent(tokenCookie.split('=')[1]);
            if (tokenValue && isValidToken(tokenValue)) {
              token = tokenValue;
              tokenSource = 'cookie';
            }
          }
        }

        // Set authorization header if we have a valid token
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
          console.log(`[CircuitBreaker] Using token from ${tokenSource} for ${fullUrl}`);
        } else {
          console.warn(
            `[CircuitBreaker] No valid token found for ${fullUrl} - checked Redux, localStorage, and cookies`,
          );
        }

        // Add tenant headers if available
        const tenantStore = (window as any).store;
        if (tenantStore) {
          const state = tenantStore.getState();
          const tenantId = state?.auth?.user?.tenantId || state?.tenant?.id;
          const subdomain = state?.tenant?.subdomain;

          if (tenantId) {
            headers.set('x-tenant-id', tenantId);
          }
          if (subdomain) {
            headers.set('x-subdomain', subdomain);
          }
        }
      } catch (error) {
        console.warn('[CircuitBreaker] Error adding auth headers:', error);
      }
    }

    // Set content type if not already set and we have a body
    if (!headers.has('Content-Type') && options?.body) {
      headers.set('Content-Type', 'application/json');
    }

    const fetchOptions = {
      ...options,
      headers,
    };

    // Direct fetch call to avoid circular imports
    console.log(`[CircuitBreaker] Making request to: ${fullUrl}`);
    console.log(`[CircuitBreaker] Request options:`, {
      method: fetchOptions.method || 'GET',
      hasBody: !!fetchOptions.body,
      headers: Object.fromEntries(headers.entries()),
    });

    return fetch(fullUrl, fetchOptions).then(async (response) => {
      console.log(`[CircuitBreaker] Response status: ${response.status} ${response.statusText}`);
      console.log(
        `[CircuitBreaker] Response headers:`,
        Object.fromEntries(response.headers.entries()),
      );

      // 402 Payment Required is expected for unpaid partners - don't count as failure
      if (response.status === 402) {
        console.log(
          `[CircuitBreaker] 402 Payment Required received - this is expected for unpaid partners, not counting as failure`,
        );
        const errorText = await response.text();
        // Return a special marker that the circuit breaker won't treat as an error
        throw new Error('__CIRCUIT_BREAKER_402_PAYMENT_REQUIRED__');
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[CircuitBreaker] Request failed with body:`, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const contentType = response.headers.get('content-type') || '';
      const result = contentType.includes('application/json')
        ? await response.json()
        : await response.text();
      console.log(
        `[CircuitBreaker] Response parsed successfully:`,
        typeof result === 'object' ? 'JSON object' : 'Text response',
      );
      return result;
    });
  };

  return breaker.execute(operation || defaultOperation);
}

// Emergency circuit breaker controls (for maintenance windows)
export const EmergencyControls = {
  // Open all circuit breakers (maintenance mode)
  openAllCircuits: () => {
    for (const breaker of circuitBreakers.values()) {
      breaker.forceOpen();
    }
    console.log('🚨 EMERGENCY: All circuit breakers opened');
  },

  // Close all circuit breakers (recovery)
  closeAllCircuits: () => {
    for (const breaker of circuitBreakers.values()) {
      breaker.forceClose();
    }
    console.log('✅ EMERGENCY: All circuit breakers closed');
  },

  // Get system health status
  getSystemHealth: () => {
    const stats = getAllCircuitBreakerStats();
    const openCircuits = Object.values(stats).filter((s) => s.state === 'OPEN').length;
    const totalCircuits = Object.keys(stats).length;

    return {
      totalCircuits,
      openCircuits,
      healthyCircuits: totalCircuits - openCircuits,
      healthPercentage:
        totalCircuits > 0 ? ((totalCircuits - openCircuits) / totalCircuits) * 100 : 100,
      circuitStats: stats,
    };
  },
};

// Development helpers
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).CircuitBreakers = {
    getStats: getAllCircuitBreakerStats,
    emergency: EmergencyControls,
    breakers: circuitBreakers,
  };
}
